import React, { useContext } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { Column, Grid, ProductionListingRow } from '@vividseats/vivid-ui-kit'
import ProductionListingRowButton from '@vividseats/vivid-ui-kit/dist/components/production-listing-row/production-listing-row-button'
import NextLink from 'next/link'

import { CATEGORY_IDS } from '@/constants'
import AnalyticsContext from '@/context/analytics'
import ProductionListContext from '@/context/productions-list'
import { useAnalytics } from '@/hooks/use-analytics'
import VariantProductionListingRowWithBadging from '@/optimizely/components/variant-production-listing-row-with-badging'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { useMlbPerformerOpeningDayDesktopFeature } from '@/optimizely/features/mlb-performer-opening-day-feature/hooks/use-mlb-performer-opening-day-desktop-feature'
import { useSellingFastDealsAvailableConcertPerformerFeature } from '@/optimizely/features/selling-fast-deals-available-concert-performer-feature/hooks'
import { useSellingFastDealsAvailableSportsHomePerformerFeature } from '@/optimizely/features/selling-fast-deals-available-sports-home-performer-feature/hooks'
import { useSellingFastDealsAvailableSportsPerformerFeature } from '@/optimizely/features/selling-fast-deals-available-sports-performer-feature/hooks'
import { useSellingFastDealsAvailableTheatrePerformerFeature } from '@/optimizely/features/selling-fast-deals-available-theatre-performer-feature/hooks'
import NcaaBasketballVariantProductionListingRow from '@/optimizely/ncaa-regions-feature/components/ncaa-basketball-variant-production-listing-row'
import { useNCAARegionsFeature } from '@/optimizely/ncaa-regions-feature/hooks/use-ncaa-regions-feature'
import type { ProductionListSelectorWithRowsInfoType } from '@/types/app-types'
import { trackSelectProduction } from '@/utils/analytics'
import { trackEvent } from '@/utils/analytics/ga360'

const ProductionListing: React.FC<ProductionListSelectorWithRowsInfoType & WithOptimizelyProps> = ({
    id,
    isExternal,
    date,
    day,
    time,
    href,
    label,
    title,
    sublabel,
    location,
    venueName,
    optimizely,
    analytics,
    categoryId,
    isSellingFast,
    isLowPricesDealsAvailable,
    isHomeOpener,
    shouldOpenInNewTab,
    onClickCapture,
}) => {
    const { state: analyticsState } = useContext(AnalyticsContext)
    // title and sublabel are swapped, except for sports productions
    const sublabelProp = categoryId !== CATEGORY_IDS.SPORTS ? title : sublabel
    const titleProp = sublabel && categoryId !== CATEGORY_IDS.SPORTS ? sublabel : title

    const {
        selectors: { analyticsSubIds },
    } = useContext(ProductionListContext)

    const { analyticsId } = useAnalytics(...analyticsSubIds)

    const [isMlbPerformerOpeningDayDesktopFeatureEnabled] = useMlbPerformerOpeningDayDesktopFeature()

    const [isSellingFastDealsAvailableConcertPerformerFeatureEnabled] =
        useSellingFastDealsAvailableConcertPerformerFeature()
    const [isSellingFastDealsAvailableTheatrePerformerFeatureEnabled] =
        useSellingFastDealsAvailableTheatrePerformerFeature()
    const [isSellingFastDealsAvailableSportsPerformerFeatureEnabled] =
        useSellingFastDealsAvailableSportsPerformerFeature()
    const [isSellingFastDealsAvailableSportsHomePerformerFeatureEnabled] =
        useSellingFastDealsAvailableSportsHomePerformerFeature()

    const isScarcityBadgingDesktopFeatureEnabled =
        isSellingFastDealsAvailableConcertPerformerFeatureEnabled ||
        isSellingFastDealsAvailableTheatrePerformerFeatureEnabled ||
        isSellingFastDealsAvailableSportsPerformerFeatureEnabled ||
        isSellingFastDealsAvailableSportsHomePerformerFeatureEnabled

    const [isNCAARegionsDesktopFeatureEnabled] = useNCAARegionsFeature(true)

    const handleClick = () => {
        onClickCapture?.()

        trackEvent('Production Selection', `${analyticsState.pageType} Page`, 'All Dates', id, false)
        optimizely?.track(OPTIMIZELY_EVENT.productionLinkClick)

        trackSelectProduction({
            item_list_name: analyticsId,
            index: analytics?.index,
            production: analytics?.production,
        })

        /* istanbul ignore next */
        if (isLowPricesDealsAvailable) {
            optimizely?.track(OPTIMIZELY_EVENT.lowPricesDealsAvailableProductionClick)
        }
    }

    const productionListingRowProps = {
        date,
        day,
        time,
        href,
        label,
        sublabel: sublabelProp,
        title: titleProp,
        mobileDivider: false,
        onClickCapture: handleClick,
        children: <ProductionListingRowButton />,
        target: shouldOpenInNewTab ? '_blank' : undefined,
        'data-testid': `production-listing-row-${title}`,
    }

    const productionListingRow: JSX.Element = (
        <Column p={1} data-testid={`production-listing-${id}`}>
            <NextLink href={href} passHref>
                {isNCAARegionsDesktopFeatureEnabled ? (
                    <NcaaBasketballVariantProductionListingRow
                        {...productionListingRowProps}
                        isDesktop
                        location={location}
                        venueName={venueName}
                        data-testid={`production-listing-row-${id}`}
                    />
                ) : isScarcityBadgingDesktopFeatureEnabled || isMlbPerformerOpeningDayDesktopFeatureEnabled ? (
                    <VariantProductionListingRowWithBadging
                        {...productionListingRowProps}
                        isSellingFast={isSellingFast}
                        isLowPricesDealsAvailable={isLowPricesDealsAvailable}
                        isHomeOpener={isHomeOpener}
                    />
                ) : (
                    <ProductionListingRow {...productionListingRowProps} data-testid={`production-listing-row-${id}`} />
                )}
            </NextLink>
        </Column>
    )

    return (
        <Grid key={id}>
            {isExternal ? (
                productionListingRow
            ) : (
                <NextLink passHref href={href}>
                    {productionListingRow}
                </NextLink>
            )}
        </Grid>
    )
}

export default withOptimizely(ProductionListing)
